import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Plus, School as SchoolIcon, Search } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const EMPTY_FORM = {
  name: "", school_type: "", address: "", postcode: "", main_telephone: "", main_email: "",
  primary_contact: "", contact_job_title: "", contact_telephone: "", contact_email: "",
  council_id: "", senco_contact_name: "", senco_contact_email: "", status: "active", notes: "",
};

const SCHOOL_TYPES = ["Mainstream Primary", "Mainstream Secondary", "Special School", "Pupil Referral Unit", "Alternative Provision", "Independent", "Other"];

export default function Schools() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("schools:manage");
  const canReadCouncils = hasPermission("councils:read");
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [councils, setCouncils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [councilFilter, setCouncilFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(params.get("new") === "1");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      if (councilFilter !== "all") params.council_id = councilFilter;
      const { data } = await api.get("/schools", { params });
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, councilFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (canReadCouncils) {
      api.get("/councils").then((r) => setCouncils(r.data.items)).catch(() => {});
    }
  }, [canReadCouncils]);

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "", school_type: s.school_type || "", address: s.address || "", postcode: s.postcode || "",
      main_telephone: s.main_telephone || "", main_email: s.main_email || "",
      primary_contact: s.primary_contact || "", contact_job_title: s.contact_job_title || "",
      contact_telephone: s.contact_telephone || "", contact_email: s.contact_email || "",
      council_id: s.council_id || "", senco_contact_name: s.senco_contact_name || "",
      senco_contact_email: s.senco_contact_email || "", status: s.status || "active", notes: s.notes || "",
    });
    setDialogOpen(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (editing) {
        await api.patch(`/schools/${editing.id}`, payload);
        toast.success("School updated");
      } else {
        await api.post("/schools", payload);
        toast.success("School created");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="schools-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Schools</h1>
          <p className="text-sm text-slate-600 mt-1">One school record, many children — schools link to their local authority.</p>
        </div>
        {canManage && (
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditing(null); setForm(EMPTY_FORM); } }}>
            <DialogTrigger asChild>
              <Button data-testid="school-add-button" className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Add school
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{editing ? `Edit ${editing.name}` : "Add school"}</DialogTitle>
                <DialogDescription>School details, contacts and linked local authority.</DialogDescription>
              </DialogHeader>
              <form onSubmit={onSave} className="space-y-4" data-testid="school-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5 sm:col-span-2"><Label>School name *</Label>
                    <Input data-testid="school-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>School type</Label>
                    <Select value={form.school_type} onValueChange={(v) => setForm({ ...form, school_type: v })}>
                      <SelectTrigger data-testid="school-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {SCHOOL_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`school-type-${t.replace(/\s+/g, "-").toLowerCase()}`}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label>Local authority</Label>
                    <Select value={form.council_id || "none"} onValueChange={(v) => setForm({ ...form, council_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="school-council-select"><SelectValue placeholder="Select local authority" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="none">None</SelectItem>
                        {councils.map((c) => (
                          <SelectItem key={c.id} value={c.id} data-testid={`school-council-${c.council_number}`}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-1.5"><Label>Address</Label>
                    <Input data-testid="school-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Postcode</Label>
                    <Input data-testid="school-postcode-input" value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Main telephone</Label>
                    <Input data-testid="school-phone-input" value={form.main_telephone} onChange={(e) => setForm({ ...form, main_telephone: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Main email</Label>
                    <Input data-testid="school-email-input" type="email" value={form.main_email} onChange={(e) => setForm({ ...form, main_email: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Primary contact</Label>
                    <Input data-testid="school-contact-input" value={form.primary_contact} onChange={(e) => setForm({ ...form, primary_contact: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Contact job title</Label>
                    <Input data-testid="school-contact-title-input" value={form.contact_job_title} onChange={(e) => setForm({ ...form, contact_job_title: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Contact telephone</Label>
                    <Input data-testid="school-contact-phone-input" value={form.contact_telephone} onChange={(e) => setForm({ ...form, contact_telephone: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Contact email</Label>
                    <Input data-testid="school-contact-email-input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>SEND / SENCO contact name</Label>
                    <Input data-testid="school-senco-name-input" value={form.senco_contact_name} onChange={(e) => setForm({ ...form, senco_contact_name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>SENCO contact email</Label>
                    <Input data-testid="school-senco-email-input" type="email" value={form.senco_contact_email} onChange={(e) => setForm({ ...form, senco_contact_email: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="school-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="active" data-testid="school-status-active">Active</SelectItem>
                        <SelectItem value="inactive" data-testid="school-status-inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
                <div className="space-y-1.5"><Label>Notes</Label>
                  <Textarea data-testid="school-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter>
                  <Button data-testid="school-save-button" type="submit" disabled={saving}
                    className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Create school"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="school-search-input" placeholder="Search by name or postcode…"
            value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="school-status-filter" className="w-full sm:w-44 h-11"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" data-testid="school-filter-all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {canReadCouncils && (
          <Select value={councilFilter} onValueChange={setCouncilFilter}>
            <SelectTrigger data-testid="school-council-filter" className="w-full sm:w-56 h-11"><SelectValue placeholder="Local authority" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all" data-testid="school-council-all">All local authorities</SelectItem>
              {councils.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-600" data-testid="schools-loading">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading schools…
        </div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-sm text-slate-600" data-testid="schools-empty">No schools found.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="schools-grid">
          {items.map((s) => (
            <Card key={s.id} data-testid={`school-card-${s.school_number}`} className="hover:border-primary/40 transition-colors duration-200">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-light">
                      <SchoolIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 leading-tight">{s.name}</p>
                      <p className="font-mono text-xs text-slate-500">{s.school_number}{s.school_type ? ` · ${s.school_type}` : ""}</p>
                    </div>
                  </div>
                  <Badge className={s.status === "active" ? "bg-ukgreen-light text-ukgreen border-transparent" : "bg-slate-100 text-slate-600 border-transparent"}>
                    {s.status}
                  </Badge>
                </div>
                <dl className="text-sm space-y-1 text-slate-600">
                  {s.postcode && <div className="flex gap-2"><dt className="text-slate-500 w-20 shrink-0">Postcode</dt><dd>{s.postcode}</dd></div>}
                  {s.council_name && <div className="flex gap-2"><dt className="text-slate-500 w-20 shrink-0">Authority</dt><dd>{s.council_name}</dd></div>}
                  {s.senco_contact_name && <div className="flex gap-2"><dt className="text-slate-500 w-20 shrink-0">SENCO</dt><dd>{s.senco_contact_name}</dd></div>}
                </dl>
                {canManage && (
                  <Button variant="outline" className="w-full h-10" data-testid={`school-edit-${s.school_number}`} onClick={() => openEdit(s)}>
                    Edit record
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
